import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import Sidebar from "./Sidebar";
import BackgroundCreateBanner from "./BackgroundCreateBanner";
import { PageAtmosphere } from "./design-system";
import { useMobileWebKeyboardViewport } from "../hooks/useMobileWebKeyboardViewport";
import { resetRouteScroll } from "../lib/resetRouteScroll";
import {
  layoutStaggerContainer,
  layoutStaggerItem,
  layoutStaggerItemReduced,
} from "./design-system/motionPresets";
import { useInstantMotion } from "../hooks/useInstantMotion";
import {
  COACH_LAYOUT_BODY_LOCK_CLASS,
  COACH_LAYOUT_ITEM_LOCK_CLASS,
  COACH_LAYOUT_MAIN_LOCK_CLASS,
  isCoachRoute,
} from "../lib/coachTranscriptLayout";

function Layout({ children }) {
  const { pathname } = useLocation();
  const skipMotion = useInstantMotion();
  const itemVariants = skipMotion ? layoutStaggerItemReduced : layoutStaggerItem;
  const mainRef = useRef(null);
  const lockCoachPhoneScroll = isCoachRoute(pathname);
  useMobileWebKeyboardViewport(mainRef);

  useEffect(() => {
    resetRouteScroll({ main: mainRef.current });
  }, [pathname]);

  return (
    <div
      className="flex relative overflow-hidden"
      style={{
        backgroundColor: "var(--surface-page, var(--bg-primary))",
        height: "100svh",
        maxHeight: "100svh",
      }}
    >
      <PageAtmosphere />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:top-2 focus:left-2 focus:z-50 focus:bg-white focus:text-black focus:px-4 focus:py-2 focus:rounded focus:shadow-lg"
      >
        Skip to main content
      </a>
      <Sidebar />
      {/*
        Main scroll container.
        - NO scroll-smooth (breaks iOS momentum / rubber-band scrolling)
        - -webkit-overflow-scrolling: touch ensures native iOS scrolling
        - overscroll-behavior-y: contain stops rubber-band from growing the document
      */}
      {/* pt-14 clears the inner header row. html padding in index.css already applies env(safe-area-inset-top). */}
      <main
        ref={mainRef}
        id="main-content"
        className={`flex-1 min-w-0 min-h-0 overflow-y-auto pt-14 md:pt-0${
          lockCoachPhoneScroll ? ` ${COACH_LAYOUT_MAIN_LOCK_CLASS}` : ''
        }`}
        style={{
          paddingBottom:
            "calc(1.5rem + env(safe-area-inset-bottom, 0px) + var(--consent-banner-offset, 0px))",
          WebkitOverflowScrolling: "touch",
          overscrollBehaviorY: "contain",
        }}
      >
        <motion.div
          variants={layoutStaggerContainer}
          initial={skipMotion ? false : "hidden"}
          animate="show"
          className={`p-4 sm:p-6 lg:p-8 pb-24 md:pb-8 instrument-press-surface${
            lockCoachPhoneScroll ? ` ${COACH_LAYOUT_BODY_LOCK_CLASS}` : ''
          }`}
        >
          <motion.div
            variants={itemVariants}
            className={lockCoachPhoneScroll ? COACH_LAYOUT_ITEM_LOCK_CLASS : undefined}
          >
            {children}
          </motion.div>
        </motion.div>
      </main>
      <BackgroundCreateBanner />
    </div>
  );
}

export default Layout;
